/**
 * Every plan in this browser, and the way to keep them safe.
 *
 * With no server, this page is also the plans' only lifeline: they exist in
 * this browser until someone downloads a backup. So it says plainly where the
 * plans live, marks the ones that have changed since their last backup, and
 * puts "back up" and "restore" beside "new plan" rather than in a settings menu
 * nobody opens until the day it is too late.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { LanguagePicker } from '../components/LanguagePicker'
import { NewPlanDialog } from '../components/NewPlanDialog'
import { ThemeSwitch } from '../components/ThemeSwitch'
import { Toast } from '../components/Toast'
import { BrandMark } from '../components/TopBar'
import { useCreatePlan } from '../hooks/useCreatePlan'
import { type BackupMark, type ConflictChoice, LOW_SPACE_BYTES, type StorageReport, api } from '../lib/api'
import { messageForError } from '../lib/i18n/errors'
import { useDateFormat, useDateTimeFormat, useSizeFormat, useT } from '../lib/i18n/I18nProvider'
import { onTabMessage } from '../lib/local/tabs'
import type { EventKind, PlanSummary } from '../lib/types'
import { usePlanStore } from '../store/planStore'

interface PendingConflict {
  incoming: PlanSummary
  existing: PlanSummary
  resolve: (choice: ConflictChoice) => void
}

export function Dashboard() {
  const t = useT()
  const formatDate = useDateFormat()
  const formatSize = useSizeFormat()
  const showToast = usePlanStore((s) => s.showToast)
  const [plans, setPlans] = useState<PlanSummary[] | null>(null)
  const [marks, setMarks] = useState<Map<string, BackupMark>>(new Map())
  const [storage, setStorage] = useState<StorageReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [choosing, setChoosing] = useState(false)
  const [chooseError, setChooseError] = useState<string | null>(null)
  const { creating, create } = useCreatePlan()
  const [busy, setBusy] = useState(false)
  const [conflict, setConflict] = useState<PendingConflict | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const refresh = useCallback(async () => {
    const [list, backups] = await Promise.all([api.listPlans(), api.backupMarks()])
    setPlans(list)
    setMarks(backups)
    // Advisory: a report that fails to arrive leaves the line out, nothing more.
    void api.storageReport().then(setStorage, () => setStorage(null))
  }, [])

  useEffect(() => {
    refresh().catch((err: unknown) => setError(messageForError(t, err, t.errors.loadPlans)))
  }, [refresh, t])

  useEffect(() => {
    // Photos left behind by deleted plans or replaced pictures. Here rather
    // than in the editor: nothing on this page can be holding one in an undo.
    void api.collectGarbage().catch(() => undefined)
    // A plan saved or deleted in another tab changes this list. An editor saves
    // about once a second while someone works, so re-read once things settle.
    let timer: ReturnType<typeof setTimeout> | undefined
    const off = onTabMessage(() => {
      clearTimeout(timer)
      timer = setTimeout(() => void refresh().catch(() => undefined), 600)
    })
    return () => {
      clearTimeout(timer)
      off()
    }
  }, [refresh])

  const openChooser = () => {
    setChooseError(null)
    setChoosing(true)
  }

  const choose = (kind: EventKind) => {
    setChooseError(null)
    create(kind).catch((err: unknown) => setChooseError(messageForError(t, err, t.errors.createPlan)))
  }

  const remove = async (plan: PlanSummary) => {
    if (!confirm(t.dashboard.confirmDelete(plan.title))) return
    try {
      await api.deletePlan(plan.id)
      setPlans((current) => current?.filter((item) => item.id !== plan.id) ?? null)
    } catch (err) {
      setError(messageForError(t, err))
    }
  }

  const duplicate = async (plan: PlanSummary) => {
    try {
      const copy = await api.duplicatePlan(plan.id, t.dashboard.copyTitle(plan.title))
      setPlans((current) => (current ? [copy, ...current] : [copy]))
    } catch (err) {
      setError(messageForError(t, err))
    }
  }

  const backup = async (planIds: string[]) => {
    setError(null)
    setBusy(true)
    try {
      await api.downloadBackup(planIds)
      setMarks(await api.backupMarks())
      showToast(t.common.backupSaved, 'success')
    } catch (err) {
      setError(messageForError(t, err))
    } finally {
      setBusy(false)
    }
  }

  const restore = async (file: File) => {
    setError(null)
    setBusy(true)
    try {
      const contents = await api.readBackup(file)
      const result = await api.restoreBackup(contents, {
        decide: (incoming, existing) =>
          new Promise<ConflictChoice>((resolve) => setConflict({ incoming, existing, resolve })),
        copyTitle: (title) => t.dashboard.copyTitle(title),
      })
      await refresh()
      showToast(t.dashboard.restored(result.restored), 'success')
    } catch (err) {
      setError(messageForError(t, err, t.errors.restoreFailed))
    } finally {
      setConflict(null)
      setBusy(false)
    }
  }

  const answer = (choice: ConflictChoice) => {
    conflict?.resolve(choice)
    setConflict(null)
  }

  const onPickBackup = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Clear first: choosing the same file twice must still fire a change event.
    event.target.value = ''
    if (file) void restore(file)
  }

  const changedSinceBackup = (plan: PlanSummary): boolean => {
    const mark = marks.get(plan.id)
    return !mark || mark.version < plan.version
  }
  const unbacked = plans?.filter(changedSinceBackup) ?? []
  const lowOnSpace = storage?.free != null && storage.free < LOW_SPACE_BYTES

  const restoreButton = (className: string) => (
    <button
      type="button"
      className={className}
      onClick={() => fileRef.current?.click()}
      disabled={busy}
    >
      {t.dashboard.restore}
    </button>
  )

  return (
    <div className="dashboard">
      <header className="dashboard-head">
        <Link to="/" className="brand">
          <BrandMark />
          <span className="brand-name">Yerbəyer</span>
        </Link>
        <div className="dashboard-head-actions">
          <div className="dashboard-prefs">
            <ThemeSwitch />
            <LanguagePicker />
          </div>
          {restoreButton('btn btn-outline')}
          <button
            type="button"
            className="btn btn-primary"
            onClick={openChooser}
          >
            {t.dashboard.newPlan}
          </button>
        </div>
      </header>

      <input
        ref={fileRef}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={onPickBackup}
      />

      <div className="dashboard-body">
        <h1 className="dashboard-title">{t.dashboard.title}</h1>
        <p className="dashboard-sub">{t.dashboard.sub}</p>

        {plans && plans.length > 0 && (
          <div
            className={`storage-note${unbacked.length > 0 || lowOnSpace ? ' storage-note--warn' : ''}`}
          >
            <span className="storage-note-text">
              <strong>
                {unbacked.length > 0
                  ? t.dashboard.unbackedPlans(unbacked.length)
                  : t.dashboard.allBackedUp}
              </strong>
              <small>{t.dashboard.storageNote}</small>
              {storage && (
                <small className="storage-note-usage">
                  {t.dashboard.storageUsed(formatSize(storage.bytes))}{' '}
                  {lowOnSpace
                    ? t.dashboard.storageLow(formatSize(storage.free ?? 0))
                    : storage.persisted === true
                      ? t.dashboard.storageKept
                      : storage.persisted === false
                        ? t.dashboard.storageMayClear
                        : ''}
                </small>
              )}
            </span>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              onClick={() => void backup(plans.map((plan) => plan.id))}
              disabled={busy}
            >
              {plans.length === 1 ? t.dashboard.backupOne : t.dashboard.backupAll}
            </button>
          </div>
        )}

        {error && <p className="dashboard-error">{error}</p>}

        {plans === null && !error && <div className="plan-grid-skeleton" aria-hidden="true" />}

        {plans?.length === 0 && (
          <motion.div
            className="empty-state"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2>{t.dashboard.emptyTitle}</h2>
            <p>{t.dashboard.emptyBody}</p>
            <div className="empty-state-actions">
              <button type="button" className="btn btn-primary" onClick={openChooser}>
                {t.dashboard.createFirst}
              </button>
              {/* Someone arriving on a new device with a backup file has zero
                  plans — this is their door. */}
              {restoreButton('btn btn-outline')}
            </div>
          </motion.div>
        )}

        {plans && plans.length > 0 && (
          <ul className="plan-grid">
            {/* Always the first cell, so it never moves as plans come and go. */}
            <li className="plan-card plan-card--new">
              <button
                type="button"
                className="plan-card-new"
                onClick={openChooser}
              >
                <span className="plan-card-new-mark" aria-hidden="true">
                  +
                </span>
                <strong>{t.dashboard.newPlan}</strong>
                <small>{t.dashboard.newPlanHint}</small>
              </button>
            </li>
            <AnimatePresence initial={false}>
              {plans.map((plan, index) => {
                const mark = marks.get(plan.id)
                return (
                  <motion.li
                    key={plan.id}
                    layout
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    /* Staggered, but capped: past ten cards the delay stops
                       growing, or the last row of a big dashboard arrives after
                       the user has already started reading the first. */
                    transition={{
                      delay: Math.min(index * 0.04, 0.3),
                      type: 'spring',
                      duration: 0.4,
                      bounce: 0.12,
                    }}
                    className="plan-card"
                  >
                    <Link to={`/plan/${plan.id}`} className="plan-card-link">
                      <div className="plan-card-head">
                        <h3>{plan.title}</h3>
                        <span className={`plan-card-kind plan-card-kind--${plan.eventKind}`}>
                          {t.events.kinds[plan.eventKind].name}
                        </span>
                      </div>
                      <p className="plan-card-meta">
                        {plan.eventDate ? formatDate(plan.eventDate) : t.dashboard.noDate}
                        {plan.venue ? ` · ${plan.venue}` : ''}
                      </p>
                      <div className="plan-card-stats">
                        <span>
                          <strong>{plan.tableCount}</strong> {t.dashboard.tableNoun(plan.tableCount)}
                        </span>
                        <span>
                          <strong>{plan.seatedCount}</strong>/{plan.seatCount}{' '}
                          {t.dashboard.seatedNoun}
                        </span>
                        <span>
                          <strong>{plan.guestCount}</strong> {t.dashboard.guestNoun(plan.guestCount)}
                        </span>
                      </div>
                      <p
                        className={`plan-card-backup${changedSinceBackup(plan) ? ' is-stale' : ''}`}
                      >
                        {!mark
                          ? t.dashboard.neverBackedUp
                          : changedSinceBackup(plan)
                            ? t.dashboard.changedSinceBackup(formatDate(mark.at))
                            : t.dashboard.backedUp(formatDate(mark.at))}
                      </p>
                    </Link>
                    <div className="plan-card-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void duplicate(plan)}
                      >
                        {t.dashboard.duplicate}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void backup([plan.id])}
                        disabled={busy}
                      >
                        {t.dashboard.backup}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => void remove(plan)}
                      >
                        {t.dashboard.delete}
                      </button>
                    </div>
                  </motion.li>
                )
              })}
            </AnimatePresence>
          </ul>
        )}
      </div>

      <RestoreConflictDialog conflict={conflict} onAnswer={answer} />
      <NewPlanDialog
        open={choosing}
        onClose={() => setChoosing(false)}
        onChoose={choose}
        creating={creating}
        error={chooseError}
      />
      <Toast />
    </div>
  )
}

/**
 * A plan in the file is already in this browser. Asked per plan, with both
 * versions' last-edited dates side by side, because the right answer depends
 * entirely on which copy is newer — and only the planner knows whether the
 * file came back from their partner or is last month's safety copy.
 */
function RestoreConflictDialog({
  conflict,
  onAnswer,
}: {
  conflict: PendingConflict | null
  onAnswer: (choice: ConflictChoice) => void
}) {
  const t = useT()
  const formatDateTime = useDateTimeFormat()

  useEffect(() => {
    if (!conflict) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onAnswer('skip')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [conflict, onAnswer])

  return (
    <AnimatePresence>
      {conflict && (
        <motion.div
          className="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          onClick={() => onAnswer('skip')}
          role="presentation"
        >
          <motion.div
            className="modal"
            initial={{ opacity: 0, scale: 0.94, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ type: 'spring', stiffness: 420, damping: 32 }}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="restore-title"
          >
            <span className="modal-eyebrow">{t.restoreDialog.eyebrow}</span>
            <h2 id="restore-title" className="modal-title">
              {t.restoreDialog.title(conflict.existing.title)}
            </h2>
            <p className="modal-lede">{t.restoreDialog.lede}</p>
            <dl className="restore-compare">
              <div>
                <dt>{t.restoreDialog.inBrowser}</dt>
                <dd>{t.restoreDialog.edited(formatDateTime(conflict.existing.updatedAt))}</dd>
              </div>
              <div>
                <dt>{t.restoreDialog.inFile}</dt>
                <dd>{t.restoreDialog.edited(formatDateTime(conflict.incoming.updatedAt))}</dd>
              </div>
            </dl>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary modal-cta"
                onClick={() => onAnswer('replace')}
              >
                {t.restoreDialog.replace}
              </button>
              <button type="button" className="btn btn-outline" onClick={() => onAnswer('copy')}>
                {t.restoreDialog.keepBoth}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => onAnswer('skip')}>
                {t.restoreDialog.skip}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
